import React, { createContext, useState, useCallback, useEffect, useRef } from 'react';
import { TenantBranding, TenantContextType } from 'types/tenant';
import { useGetBranding } from 'api/tenant';
import useAuth from 'hooks/useAuth';
import { saveTenantUniqueCode } from 'utils/tenantRouting';

const initialState: TenantContextType = {
    branding: null,
    isLoading: false,
    error: null,
    fetchBranding: async () => { },
    clearBranding: () => { }
};

const TenantContext = createContext<TenantContextType>(initialState);

export const TenantProvider = ({ children }: { children: React.ReactElement }) => {
    const [branding, setBranding] = useState<TenantBranding | null>(() => {
        try {
            const cached = localStorage.getItem('tenantBranding');
            return cached ? JSON.parse(cached) : null;
        } catch {
            return null;
        }
    });
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const { user } = useAuth();
    const lastFetchedUniqueCodeRef = useRef<string | null>(null);

    const fetchBranding = useCallback(async (keyword: string) => {
        setIsLoading(true);
        setError(null);
        try {
            const data = await useGetBranding(keyword);
            setBranding(data);
            localStorage.setItem('tenantBranding', JSON.stringify(data));
            if (data?.uniqueCode || keyword) {
                saveTenantUniqueCode(data?.uniqueCode || keyword);
            }
            lastFetchedUniqueCodeRef.current = keyword;
        } catch (err: any) {
            setError(err.message || 'Failed to fetch tenant branding');
            setBranding(null);
            localStorage.removeItem('tenantBranding');
            lastFetchedUniqueCodeRef.current = null;
            throw err;
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        // Only fetch if:
        // 1. User has a uniqueCode
        // 2. We haven't already fetched for this uniqueCode
        // 3. Either we don't have branding OR the branding belongs to a different uniqueCode
        if (
            user?.uniqueCode &&
            lastFetchedUniqueCodeRef.current !== user.uniqueCode &&
            (!branding || branding.uniqueCode !== user.uniqueCode)
        ) {
            fetchBranding(user.uniqueCode);
        }
    }, [user?.uniqueCode, fetchBranding]);

    const clearBranding = useCallback(() => {
        setBranding(null);
        setError(null);
        localStorage.removeItem('tenantBranding');
        lastFetchedUniqueCodeRef.current = null;
    }, []);

    return (
        <TenantContext value={{ branding, isLoading, error, fetchBranding, clearBranding }}>
            {children}
        </TenantContext>
    );
};

export default TenantContext;
